/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { myHeaders } from "../../../config/axios";
import { URL_API } from "../../../config/default";
import Error from "../../Error/Index";
import StatisticsOverview from "../../../components/Admin/statistics-overview";
import SimpleRevenueChart from "../../../components/Admin/simple-revenue-chart";
import SimpleBookingsPeriodChart from "../../../components/Admin/simple-bookings-period-chart";
import SimpleBillingTable from "../../../components/Admin/simple-billing-table";
import { useAdminContext } from "../../../contexts/AdminProvider";
import { Skeleton } from "../../../components/UI/Skeleton";

const Ingresos = () => {
  const { me } = useAdminContext();

  const [filter, setFilter] = useState({
    status: "approved",
  });

  const { isPending: isPendingBookings, data: statisticsBookings } = useQuery({
    queryKey: ["estadisticas"],
    queryFn: () => {
      const authorization = new Headers();
      authorization.append("Authorization", myHeaders);

      return fetch(`${URL_API}/reservations/statistics`, {
        method: "GET",
        headers: {
          Authorization: myHeaders,
        },
      }).then((res) => res.json());
    },
  });

  const { isPending: isPendingProperties, data: statisticsProperties } =
    useQuery({
      queryKey: ["estadisticas-alojamientos"],
      queryFn: () => {
        const authorization = new Headers();
        authorization.append("Authorization", myHeaders);

        return fetch(`${URL_API}/properties/statistics`, {
          method: "GET",
          headers: {
            Authorization: myHeaders,
          },
        }).then((res) => res.json());
      },
    });

  const { isPending: isPendingStatistics, data: myStatistics } = useQuery({
    queryKey: ["mis-estadisticas"],
    queryFn: () => {
      const authorization = new Headers();
      authorization.append("Authorization", myHeaders);

      return fetch(`${URL_API}/reservations/my-statistics`, {
        method: "GET",
        headers: {
          Authorization: myHeaders,
        },
      }).then((res) => res.json());
    },
  });

  const { isPending, error, data } = useQuery({
    queryKey: ["index-reservas", filter],
    queryFn: () => {
      const authorization = new Headers();
      authorization.append("Authorization", myHeaders);

      const params = new URLSearchParams(filter);

      return fetch(`${URL_API}/reservations?${params}`, {
        method: "GET",
        headers: {
          Authorization: myHeaders,
        },
      }).then((res) => res.json());
    },
  });

  if (isPendingBookings || isPendingProperties || isPendingStatistics)
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-5 w-96" />
        </div>

        {/* Statistics Overview Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm border space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>

        {/* Additional statistics cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm border space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart Skeleton */}
          <div className="bg-white rounded-lg p-6 shadow-sm border space-y-4">
            <div className="flex justify-between items-center">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-8 w-24" />
            </div>
            <Skeleton className="h-64 w-full" />
          </div>

          {/* Bookings Chart Skeleton */}
          <div className="bg-white rounded-lg p-6 shadow-sm border space-y-4">
            <div className="flex justify-between items-center">
              <Skeleton className="h-6 w-44" />
              <Skeleton className="h-8 w-24" />
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        </div>

        {/* Billing Table Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 sm:p-6 border-b space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            {/* Table Header */}
            <div className="grid grid-cols-6 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
            {/* Table Rows */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="grid grid-cols-6 gap-4">
                {[1, 2, 3, 4, 5, 6].map((j) => (
                  <Skeleton key={j} className="h-4 w-full" />
                ))}
              </div>
            ))}
            {/* Pagination Skeleton */}
            <div className="flex justify-between items-center pt-4">
              <Skeleton className="h-4 w-32" />
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-8 w-8" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  if (error) return <Error />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Estadísticas
        </h1>
        <p className="text-gray-600 mt-2">
          Análisis detallado de ingresos, reservas y facturación
        </p>
      </div>

      {/* Overview Cards */}
      <StatisticsOverview
        ingresos_total_aprobado={
          statisticsBookings.data.data.ingresos_total_aprobado
        }
        ingresos_total_pendiente={
          statisticsBookings.data.data.ingresos_total_pendiente
        }
        reservas_activas={statisticsBookings.data.data.reservas_activas}
        tasa_cancelacion={statisticsBookings.data.data.tasa_cancelacion}
        huespedes_total={statisticsBookings.data.data.huespedes_total}
        comisiones_total={statisticsBookings.data.data.comisiones_total}
        alojamientos_activos={statisticsProperties.data["alojamientos-activos"]}
      />

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimpleRevenueChart
          monthlyData={statisticsBookings.data["ingresos-mes"]}
          weeklyData={statisticsBookings.data["periodos-demanda"]}
        />
        <SimpleBookingsPeriodChart
          accommodationData={statisticsProperties.data["alojamientos-data"]}
          seasonalData={statisticsProperties.data["temporada-demanda"]}
        />
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 sm:p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Datos de Facturación
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestión detallada de ingresos, cancelaciones y pendientes
          </p>
        </div>
        <SimpleBillingTable
          myStatistics={myStatistics.data}
          data={isPending ? { data: [] } : data}
          setFilter={setFilter}
          filter={filter}
          me={me}
        />
      </div>
    </div>
  );
};

export default Ingresos;
